import React from 'react';
import { Typography } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { useTheme } from '@mui/material';

export interface FooterSection {
  /** Main value (can be number, string, or React element) */
  value?: React.ReactNode;

  /** Optional label displayed under the value */
  label?: string;

  /** Optional icon or element displayed before the value */
  icon?: React.ReactNode;
  
  /** Optional tooltip or click action */
  onClick?: () => void;

  /** Custom colors */
  valueColor?: string;
  labelColor?: string;
}

interface StandardNodeFooterProps {
  /** Up to three sections to display (left, center, right) */
  leftSection?: FooterSection;
  centerSection?: FooterSection;
  rightSection?: FooterSection;

  /** Background and text colors */
  backgroundColor?: string;
  borderColor?: string;

  /** Layout / styling options */
  padding?: string | number;
  justify?: 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around';
  borderTop?: boolean;
}

const StandardNodeFooter: React.FC<StandardNodeFooterProps> = ({
  leftSection,
  centerSection,
  rightSection,
  backgroundColor,
  borderColor,
  padding = '6px',
  justify = 'center',
  borderTop = true,
}) => {
  const theme = useTheme();

  const renderSection = (section?: FooterSection) => {
    if (!section) return <div />;

    return (
      <div
        style={{
          textAlign: 'center',
          cursor: section.onClick ? 'pointer' : 'default',
          color: section.valueColor || theme.custom.nodes.text.primary.dark,
        }}
        onClick={section.onClick}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
          }}
        >
          <Typography
            variant="body1"
            component="div"
            sx={{
              fontWeight: 600,
              color: section.valueColor || theme.custom.nodes.text.primary.dark,
            }}
          >
            {section.value ?? '--'}
          </Typography>
        </div>

        {section.label && (
          <>
            {section.icon && <span>{section.icon}</span>}
            <Typography
              variant="caption"
              component="div"
              sx={{
                color: section.labelColor || theme.custom.nodes.text.secondary.dark,
                opacity: 0.9,
              }}
            >
              {section.label}
            </Typography>
          </>
        )}
      </div>
    );
  };
  return (
    <div
      style={{
        backgroundColor: backgroundColor || theme.custom.nodes.footer.main.dark,
        padding,
        borderTop: borderTop
          ? `1px solid ${borderColor || theme.custom.utility.divider.dark}`
          : undefined,
        color: theme.custom.nodes.text.primary.dark,
      }}
    >
      <Grid2 container justifyContent={justify} alignItems="center" textAlign="center">
        <Grid2 size={4}>{renderSection(leftSection)}</Grid2>
        <Grid2 size={4}>{renderSection(centerSection)}</Grid2>
        <Grid2 size={4}>{renderSection(rightSection)}</Grid2>
      </Grid2>
    </div>
  );
};

export default StandardNodeFooter;
